import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Shield, Loader2 } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const formSchema = z.object({
  reason: z.string().min(1, "Reason is required"),
  duration_minutes: z.number().min(15, "Duration must be at least 15 minutes").max(240, "Duration cannot exceed 4 hours"),
});

interface RequestPrivilegeAccessProps {
  deviceId: string;
  enrollmentToken: string | null;
}

export function RequestPrivilegeAccess({ deviceId, enrollmentToken }: RequestPrivilegeAccessProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reason: "",
      duration_minutes: 60,
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setIsSubmitting(true);
      console.log('Requesting privilege elevation for device:', deviceId);

      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) {
        toast.error("You must be logged in to request privileges");
        return;
      }

      // Get enrollment token with proper authorization
      const { data: enrollmentToken, error: tokenError } = await supabase.rpc(
        'get_device_enrollment_token', 
        { p_device_id: deviceId }
      );

      if (tokenError || !enrollmentToken) {
        console.error('No enrollment token found for device:', deviceId, tokenError);
        toast.error('Device enrollment token not found or access denied');
        return;
      }

      const { data, error } = await supabase.functions.invoke('request-privilege', {
        body: {
          device_id: deviceId,
          enrollment_token: enrollmentToken,
          requested_level: 'admin',
          reason: values.reason,
          duration_minutes: values.duration_minutes,
          user_id: session.session.user.id
        }
      });

      if (error) throw error;

      console.log('Privilege request response:', data);
      toast.success('Privilege escalation request submitted successfully');
      setIsDialogOpen(false);
      form.reset();
    } catch (error) {
      console.error('Error requesting privileges:', error);
      toast.error('Failed to submit privilege request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full justify-start">
          <Shield className="mr-2 h-4 w-4" />
          Request Admin Access
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request Admin Access</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Reason</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Why do you need admin access?"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Provide a detailed explanation for your request
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="duration_minutes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Duration (minutes)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={15}
                      max={240}
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value))}
                    />
                  </FormControl>
                  <FormDescription>
                    How long do you need admin access? (15-240 minutes)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}